/**
 * LLD Validation utilities.
 * Pure functions for validating entities, members, relationships, and file trees.
 */

export interface ValidationResult {
 valid: boolean;
 error?: string;
}

/**
 * Validates an entity name and description against business rules.
 *
 * Rules:
 * - Name must not be empty
 * - Name must not exceed 50 characters
 * - Description must not exceed 200 characters
 * - Name must not duplicate an existing entity (case-insensitive)
 * - Maximum 50 entities allowed
 *
 * @param name - The entity name to validate
 * @param description - The entity description to validate
 * @param existingEntities - Array of existing entity names
 * @returns ValidationResult indicating success or failure with error message
 */
export function validateEntity(
 name: string,
 description: string,
 existingEntities: string[]
): ValidationResult {
 if (!name || name.trim().length === 0) {
  return { valid: false, error: 'Entity name cannot be empty' };
 }

 if (name.length > 50) {
  return { valid: false, error: 'Entity name cannot exceed 50 characters' };
 }

 if (description.length > 200) {
  return { valid: false, error: 'Entity description cannot exceed 200 characters' };
 }

 const nameLower = name.trim().toLowerCase();
 const isDuplicate = existingEntities.some(
  (existing) => existing.trim().toLowerCase() === nameLower
 );
 if (isDuplicate) {
  return { valid: false, error: 'An entity with this name already exists' };
 }

 if (existingEntities.length >= 50) {
  return { valid: false, error: 'Maximum of 50 entities reached' };
 }

 return { valid: true };
}

/**
 * Validates a member (attribute or method) name against business rules.
 *
 * Rules:
 * - Name must not be empty
 * - Name must not duplicate an existing member name on the same entity
 *
 * @param name - The member name to validate
 * @param existingMembers - Array of existing member names on the same entity
 * @returns ValidationResult indicating success or failure with error message
 */
export function validateMember(
 name: string,
 existingMembers: string[]
): ValidationResult {
 if (!name || name.trim().length === 0) {
  return { valid: false, error: 'Member name cannot be empty' };
 }

 const isDuplicate = existingMembers.some(
  (existing) => existing === name
 );
 if (isDuplicate) {
  return { valid: false, error: 'A member with this name already exists on this entity' };
 }

 return { valid: true };
}

/**
 * Checks whether a relationship can be added between source and target.
 *
 * Rules:
 * - Cannot create a duplicate relationship (same source and same target)
 *
 * @param source - The source entity ID
 * @param target - The target entity ID
 * @param existingRelationships - Array of existing relationships with sourceEntityId and targetEntityId
 * @returns ValidationResult indicating success or failure with error message
 */
export function canAddRelationship(
 source: string,
 target: string,
 existingRelationships: { sourceEntityId: string; targetEntityId: string }[]
): ValidationResult {
 const isDuplicate = existingRelationships.some(
  (rel) => rel.sourceEntityId === source && rel.targetEntityId === target
 );
 if (isDuplicate) {
  return { valid: false, error: 'A relationship between these entities in this direction already exists' };
 }

 return { valid: true };
}

/**
 * Validates file tree constraints for the Code Editor.
 *
 * Rules:
 * - Maximum 20 files
 * - Maximum 5 directory nesting levels
 *
 * @param fileCount - Current number of files in the tree
 * @param depth - Current maximum directory depth
 * @returns ValidationResult indicating success or failure with error message
 */
export function validateFileTree(
 fileCount: number,
 depth: number
): ValidationResult {
 if (fileCount > 20) {
  return { valid: false, error: 'Maximum of 20 files allowed' };
 }

 if (depth > 5) {
  return { valid: false, error: 'Maximum directory nesting depth of 5 levels allowed' };
 }

 return { valid: true };
}

// ─── Custom LLD Problem validation (mirrors the backend CustomLLDProblemDto) ──────

/** Allowed difficulty values; absent (undefined) is also valid. */
export const CUSTOM_PROBLEM_DIFFICULTIES = [
 'Beginner',
 'Intermediate',
 'Advanced',
 'Expert',
] as const;

/** Allowed category values; absent (undefined) is also valid. */
export const CUSTOM_PROBLEM_CATEGORIES = [
 'Object Modeling',
 'System Management',
 'Payment and Splitting',
 'Booking and Reservation',
 'Real-Time Services',
] as const;

/** Field-level limits, kept in lockstep with the server DTO. */
export const CUSTOM_PROBLEM_LIMITS = {
 titleMax: 200,
 descriptionMax: 5000,
 expectedEntitiesMax: 50,
 expectedRelationshipsMax: 100,
 testCasesMax: 50,
} as const;

/** The subset of fields the validator inspects (a structural mirror of the DTO). */
export interface CustomProblemValidationInput {
 title?: string;
 description?: string;
 difficulty?: string;
 category?: string;
 clarifications?: unknown[];
 expectedEntities?: unknown[];
 expectedRelationships?: unknown[];
 testCases?: unknown[];
}

export interface CustomProblemValidationResult {
 valid: boolean;
 /** Map of field name → human-readable violated-constraint message. */
 errors: Record<string, string>;
}

/**
 * Pure predicate mirroring the server-side validation rules:
 * - title: non-empty, <= 200 chars
 * - description: non-empty, <= 5000 chars
 * - difficulty / category: must be a member of the allowed enum when present (absent is OK)
 * - collection caps: expectedEntities <= 50, expectedRelationships <= 100, testCases <= 50
 *
 * Returns `{ valid, errors }` where `errors` names each violated constraint by field.
 */
export function validateCustomProblem(
 input: CustomProblemValidationInput
): CustomProblemValidationResult {
 const errors: Record<string, string> = {};

 // title — required, non-empty, within cap
 const title = input.title ?? '';
 if (title.trim().length === 0) {
  errors.title = 'Title is required';
 } else if (title.length > CUSTOM_PROBLEM_LIMITS.titleMax) {
  errors.title = `Title cannot exceed ${CUSTOM_PROBLEM_LIMITS.titleMax} characters`;
 }

 // description — required, non-empty, within cap
 const description = input.description ?? '';
 if (description.trim().length === 0) {
  errors.description = 'Description is required';
 } else if (description.length > CUSTOM_PROBLEM_LIMITS.descriptionMax) {
  errors.description = `Description cannot exceed ${CUSTOM_PROBLEM_LIMITS.descriptionMax} characters`;
 }

 // difficulty — valid enum member when present
 if (
  input.difficulty !== undefined &&
  input.difficulty !== '' &&
  !(CUSTOM_PROBLEM_DIFFICULTIES as readonly string[]).includes(input.difficulty)
 ) {
  errors.difficulty = 'Difficulty must be one of the allowed values';
 }

 // category — valid enum member when present
 if (
  input.category !== undefined &&
  input.category !== '' &&
  !(CUSTOM_PROBLEM_CATEGORIES as readonly string[]).includes(input.category)
 ) {
  errors.category = 'Category must be one of the allowed values';
 }

 // collection caps
 if ((input.expectedEntities?.length ?? 0) > CUSTOM_PROBLEM_LIMITS.expectedEntitiesMax) {
  errors.expectedEntities = `Expected entities must contain at most ${CUSTOM_PROBLEM_LIMITS.expectedEntitiesMax} items`;
 }
 if ((input.expectedRelationships?.length ?? 0) > CUSTOM_PROBLEM_LIMITS.expectedRelationshipsMax) {
  errors.expectedRelationships = `Expected relationships must contain at most ${CUSTOM_PROBLEM_LIMITS.expectedRelationshipsMax} items`;
 }
 if ((input.testCases?.length ?? 0) > CUSTOM_PROBLEM_LIMITS.testCasesMax) {
  errors.testCases = `Test scenarios must contain at most ${CUSTOM_PROBLEM_LIMITS.testCasesMax} items`;
 }

 return { valid: Object.keys(errors).length === 0, errors };
}
